import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  useColorModeValue,
  Badge,
  Divider,
  Progress,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Button,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, TriangleDownIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const AdmissionStatusAnalysis = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBlockBg = useColorModeValue('gray.50', 'gray.800');
  const supportingEvidenceBg = useColorModeValue('green.50', 'green.900');
  const conflictingEvidenceBg = useColorModeValue('red.50', 'red.900');

  const fetchInitialAdmissionStatus = async () => {
    if (!caseId || !accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await callApi(`/api/cases/${caseId}/admission-status`, accessToken);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      setResults(data);
    } catch (err) {
      console.error('Error fetching initial admission status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialAdmissionStatus();
  }, [caseId, accessToken]);

  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await callApi(`/api/cases/${caseId}/run-analysis`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_type: 'ADMISSION_STATUS',
          force_rerun: true,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || 'Failed to run analysis.');
      }

      const newResultsPayload = {
        status: 'success',
        case_id: data.case_id,
        admission_status: data.results,
        metadata: {
          extracted_at: new Date().toISOString(),
          is_current: true,
        },
      };

      setResults(newResultsPayload);
      toast({
        title: 'Analysis Complete',
        description: 'Admission status has been re-analyzed successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error running analysis:', err);
      setError(err.message);
      toast({
        title: 'Analysis Failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getConfidenceAttributes = (score) => {
    if (score >= 8) return { colorScheme: 'green', level: 'High' };
    if (score >= 5) return { colorScheme: 'yellow', level: 'Medium' };
    return { colorScheme: 'red', level: 'Low' };
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text fontSize="lg">Loading Admission Status...</Text>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    if (error) {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Error Fetching Data</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Box>
        </Alert>
      );
    }

    if (!results || results.status === 'not_found') {
      return (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>No Analysis Found</AlertTitle>
            <AlertDescription>
              No admission status analysis has been performed for this case yet.
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    const { admission_status, metadata } = results;
    const confidenceAttrs = getConfidenceAttributes(admission_status.confidence);

    return (
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Admission Status Results</Heading>
            <Badge
              colorScheme={results.status === 'success' ? 'green' : 'red'}
              fontSize="sm"
              px={3}
              py={1}
            >
              {results.status.toUpperCase()}
            </Badge>
          </HStack>
          {metadata?.extracted_at && (
            <Text fontSize="sm" color="gray.500" mt={1}>
              Extracted on: {new Date(metadata.extracted_at).toLocaleString()}
            </Text>
          )}
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Primary Status and Confidence */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Card bg={cardBg} borderColor={borderColor} p={4}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.500">PRIMARY STATUS</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">{admission_status.primary_status || 'Unknown'}</Text>
                </Card>
                <Card bg={cardBg} borderColor={borderColor} p={4}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.500">CONFIDENCE</Text>
                    <HStack>
                        <Progress value={admission_status.confidence * 10} colorScheme={confidenceAttrs.colorScheme} size="lg" flex="1" borderRadius="md" />
                        <Badge colorScheme={confidenceAttrs.colorScheme} fontSize="md">{confidenceAttrs.level} ({admission_status.confidence}/10)</Badge>
                    </HStack>
                </Card>
            </SimpleGrid>

            {/* Discussion */}
            <Box>
              <Heading size="sm" mb={2}>Analyst Discussion</Heading>
              <Box p={4} bg={codeBlockBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <Text whiteSpace="pre-wrap">{admission_status.discussion || 'No discussion provided.'}</Text>
              </Box>
            </Box>

            <Divider />

            {/* Evidence Sections */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* Supporting Evidence */}
              <Box>
                <Heading size="sm" mb={2}>Supporting Evidence</Heading>
                {admission_status.supporting_evidence && admission_status.supporting_evidence.length > 0 ? (
                  <List spacing={3}>
                    {admission_status.supporting_evidence.map((quote, index) => (
                      <ListItem key={index} bg={supportingEvidenceBg} p={3} borderRadius="md">
                        <ListIcon as={CheckCircleIcon} color="green.500" />
                        <Text as="i" display="inline">"{quote}"</Text>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Text color="gray.500" fontStyle="italic">No supporting evidence found.</Text>
                )}
              </Box>

              {/* Conflicting Evidence */}
              <Box>
                <Heading size="sm" mb={2}>Conflicting Evidence</Heading>
                {admission_status.conflicting_evidence && admission_status.conflicting_evidence.length > 0 ? (
                  <List spacing={3}>
                    {admission_status.conflicting_evidence.map((quote, index) => (
                      <ListItem key={index} bg={conflictingEvidenceBg} p={3} borderRadius="md">
                        <ListIcon as={WarningIcon} color="red.500" />
                        <Text as="i" display="inline">"{quote}"</Text>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Text color="gray.500" fontStyle="italic">No conflicting evidence found.</Text>
                )}
              </Box>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading as="h1" size="xl" mb={2}>
              Admission Status Analysis
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Detailed analysis of the patient's admission status based on clinical documentation.
            </Text>
          </Box>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="lg">
                  <strong>Case ID:</strong> {caseId}
                </Text>
                <Button
                  colorScheme="blue"
                  onClick={runAnalysis}
                  isLoading={isAnalyzing}
                  loadingText="Analyzing..."
                  isDisabled={loading}
                >
                  {results && results.status !== 'not_found' ? 'Re-run Analysis' : 'Run Analysis'}
                </Button>
              </HStack>
            </CardBody>
          </Card>

          {renderContent()}
        </VStack>
      </Container>
    </Box>
  );
};

export default AdmissionStatusAnalysis;